import 'isomorphic-fetch'
import {
  // isToLoadFromFixture,
  enableProxy,
  isToEnableSSR,
  API_ABORT_DELAY,
  getActiveAppConfig,
  getLocalStorage,
  getSessionStorage,
  setLocalStorage,
  deleteFromLocalStorage,
  setCookie,
  getCookie,
  deleteCookie,
  isB2BAccount,
  isB2BAdmin,
  isB2BUser,
  isB2B2C,
  isB2B2CAdmin,
  enableB2B2CUserInviteCreate,
  isenableUserInvite,
  APPConfig,
} from 'config/appConfig'
import { getEndPoints } from 'src/apiEndPoints'
import { storeContainer } from 'src/models/Store'
import { application, IS_BROWSER } from './application'
// import { CS_ID, CS_TOKEN } from 'config/appConfig'
import {
  checkMysiteOrPersonalOffer,
  replaceLocaleInURL,
  trackErrorInInstana,
  enableFeatureFlagtoApi,
  getCookiesFeatureOverride,
  isExpressCart,
  enableMysiteRedesignFeature,
  enablePORedesignFeature,
  enableCSErrorCodeMessage,
} from 'src/utils/commonUtils'
import { getLocaleCodeFromUrl } from 'src/utils/localeUtils'
import { pageNames } from 'src/routes/pathParams'
import { isToShowOverrideSubscription } from 'src/utils/multipleSubscriptionUtils'
import { getPOAnalyticsSessionId } from 'src/utils/poAnalyticsUtils'
import {
  SPONSOR,
  PERSONAL_OFFER_SESSION_KEY,
  PERSONAL_OFFER_V2_KEY,
} from './constant'
import { getContentStackErrorMessage } from 'src/utils/apiErrorToast'

/**
 * Checks if a response from the API indicates a successful request.
 *
 * @param {Object} response - The response object from the API request.
 * @returns {boolean} True if the response status is >= 200 and < 300, false otherwise.
 */
function isSuccessStatus(response) {
  if (response) {
    const status = response?.status
    return status && status >= 200 && status < 300
  }
}
/**
 * Generates the API configuration object from the provided apiConfig.
 *
 * @param {Object} apiConfig - The API config object containing url, method and endPointName
 * @returns {Object} The API configuration object with apiUrl and method populated
 */

function generateAPIConfig(apiConfig) {
  const END_POINTS = getEndPoints()
  const { url, method, endPointName } = apiConfig

  return {
    apiUrl: url || END_POINTS[endPointName].url,
    method: method || END_POINTS[endPointName].method,
  }
}

/**
 * Checks if session headers should be ignored for the given endpoint.
 *
 * For B2B accounts, session headers are always ignored.
 * For B2C accounts, session headers are ignored for certain endpoints like
 * topCategory, getCatalog, getProducts, search.
 *
 * @param {string} endPointName - The name of the endpoint being called
 * @returns {boolean} True if session headers should be ignored, false otherwise
 */
function ignoreSessionHeaders(endPointName) {
  if (isB2BAccount()) {
    return true
  }
  return endPointName == 'topCategory' ||
    endPointName == 'getCatalog' ||
    endPointName == 'getProducts' ||
    endPointName == 'search'
    ? false
    : true
}

/**
 * Merges common headers and headers from cookies into a single headers object.
 *
 * For the specified endpoints, it will read the cookie values for the headersToPassFromCookieToAPI
 * and add them to the headers object. Otherwise the sessionId header will be ignored.
 *
 * @param {Object} headers - The request headers object
 * @param {string} endPointName - The name of the endpoint being called
 * @param {Object} appConfig - The app config containing commonHeaders and headersToPassFromCookieToAPI
 * @returns {Object} The merged headers object
 */
function getCommonHeaders(headers, endPointName, appConfig) {
  const { commonHeaders = {}, headersToPassFromCookieToAPI = [] } = appConfig

  headersToPassFromCookieToAPI.forEach(value => {
    const cookieValue = getCookie(value)
    if (cookieValue && ignoreSessionHeaders(endPointName)) {
      headers[value] = cookieValue
    } else {
      console.log('sessionId ignored for >>> ', endPointName)
    }
  })

  return {
    ...commonHeaders,
    ...headers,
  }
}

/**
 * Tracks API errors through Instana by emitting a custom event.
 *
 * @param {Object} options - The options object
 * @param {Object} options.apiConfig - The API config object
 * @param {number} options.statusCode - The HTTP status code
 * @param {string} options.error - The error message
 * @param {Object} options.headers - The HTTP headers
 * @param {string} options.responseMessage - The response message
 * @param {string} options.apiUrl - The API URL
 */
function trackApiErrorThroughInstana(options) {
  const {
    apiConfig,
    statusCode,
    error = '',
    headers,
    responseMessage = '',
    apiUrl: url = '',
  } = options || {}
  const { endPointName = '', queryParams = {} } = apiConfig
  let customEventPrefix = 'Custom_Event_'
  let errMessage = customEventPrefix + endPointName
  let isToReportError = true
  // We need access to read the response headers
  // const spanId = headers?.get?.('x-b3-spanid') || ''
  // const traceId = headers?.get?.('x-b3-traceid') || ''

  const hasInstanaReporter =
    typeof window !== 'undefined' && typeof window.ineum === 'function'

  if (hasInstanaReporter) {
    // commenting it to identify all the errors via Instana custom event
    // if (statusCode == '400' || statusCode == '401' || statusCode == '404') {
    //   isToReportError = false
    // }

    if (isToReportError) {
      const errorReport =
        `API error from ${IS_BROWSER ? 'BROWSER' : 'SERVER'} ` + endPointName
      const errorData = {
        url,
        statusCode,
        fetchError: error,
        responseMessage,
        ...apiConfig,
      }
      console.warn(errorReport, errorData)
      if (hasInstanaReporter) {
        window.ineum('reportEvent', errorReport, {
          meta: errorData,
        })
      }
    }
  }
}

/**
 * Increments the count of active fetches.
 */
const incrementFetchCount = () => {
  application.activeFetches += 1
}

/**
 * Decrements the count of active fetches.
 */
const decrementFetchCount = () => {
  if (application.activeFetches > 0) {
    application.activeFetches -= 1
  }
}

/**
 * Checks if there are any ongoing fetches.
 * @returns {boolean} True if there are ongoing fetches, false otherwise.
 */
const hasOngoingFetches = () => {
  return application.activeFetches > 0
}

/**
 * Resolves the currently active Personal Offer / offerId from the same
 * priority list used for outbound PO headers: sessionStorage `personalOffer`
 * (offerId → pitchID → shareLink.id), then `personalOffer_v2.offerId`.
 *
 * @returns {string}
 */
function resolveActivePOOfferId() {
  let personalOfferData =
    sessionStorage.getItem(PERSONAL_OFFER_SESSION_KEY) || '{}'
  try {
    personalOfferData = JSON.parse(personalOfferData)
  } catch (e) {
    personalOfferData = {}
  }
  const personalOfferV2 = getLocalStorage(PERSONAL_OFFER_V2_KEY) || {}
  return (
    personalOfferData?.offerId ||
    personalOfferData?.pitchID ||
    personalOfferData?.shareLink?.id ||
    personalOfferV2?.offerId ||
    ''
  )
}

/**
 * Resolves Personal Offer / analytics IDs from session & local storage and
 * applies them as outbound headers (sponsorId, offerId, sessionId,
 * analyticsId). Only sets a header when a non-empty value is resolved and
 * when the header has not already been set by an earlier code path.
 *
 * @param {Object} headers - mutable headers object the values are written to
 * @param {Object} [opts]
 * @param {boolean} [opts.includeConvSponsor=false] - when true, falls back to
 *   the `conv-sponsor` session storage value for sponsorId (used by search
 *   endpoints where the conv sponsor is the canonical attribution source).
 */
function applyPOAnalyticsHeaders(headers, opts = {}) {
  const { includeConvSponsor = false } = opts
  const sponsor = getSessionStorage(SPONSOR) || ''
  let personalOfferData =
    sessionStorage.getItem(PERSONAL_OFFER_SESSION_KEY) || '{}'
  try {
    personalOfferData = JSON.parse(personalOfferData)
  } catch (e) {
    personalOfferData = {}
  }
  const personalOfferV2 = getLocalStorage(PERSONAL_OFFER_V2_KEY) || {}

  const offerId =
    personalOfferData?.offerId ||
    personalOfferData?.pitchID ||
    personalOfferData?.shareLink?.id ||
    personalOfferV2?.offerId ||
    ''
  // `po_audit_sessionId` is an `{ offerId: analyticsSessionId }` map keyed by
  // offerId so each open offer keeps its own analytics session.
  const sessionId =
    getPOAnalyticsSessionId(offerId) || personalOfferV2?.sessionId || ''
  const sponsorId =
    personalOfferData?.sapId ||
    sponsor ||
    (includeConvSponsor ? getSessionStorage('conv-sponsor') || '' : '')

  if (offerId && !headers['x-nuskin-offerid']) {
    headers['x-nuskin-offerid'] = offerId
  }
  if (sponsorId && !headers['x-nuskin-sponsorid']) {
    headers['x-nuskin-sponsorid'] = sponsorId
  }
  if (enablePORedesignFeature()) {
    delete headers['x-nuskin-sessionId']
    if (sessionId && !headers['x-nuskin-analyticsId']) {
      headers['x-nuskin-analyticsId'] = sessionId
    }
  } else {
    delete headers['x-nuskin-analyticsId']
    if (sessionId && !headers['x-nuskin-sessionId']) {
      headers['x-nuskin-sessionId'] = sessionId
    }
  }
}

/**
 * Fetches API response based on given API config.
 *
 * @param {Object} apiConfig - API config object containing request details like endpoint name, headers, query params etc.
 */
async function fetchResponseFromAPI(apiConfig) {
  const {
    pathParams,
    queryParams = {},
    // isToStoreInLocal,
    endPointName,
    postData,
    ignoreCommonHeader = false,
    contextName,
    contextUrlPathFromSSR = '',
    contextQueryParamsFromSSR = '',
    isToSendLocaleInQueryParam = true,
    mode,
    isToSendStoreId = true,
    deleteContentType = false,
    credentials = 'include',
    isToDeleteHeader = false,
    isToIncludeCommomHeaders = true,
    addPathParams,
    curLocaleFromUrl,
    sessionCookies,
    cartType = '',
    keepAlive = false,
    isFromInit = false,
    isToRemoveCommonHeaders = [],
    csEntryName,
    csQueryString,
    suppressContentStackErrorToast = false,
  } = apiConfig

  console.log(`endPointName >>> `, endPointName)

  let { headers = {} } = apiConfig

  application.IsAppLoading = !application.isCallInProgress

  if (IS_BROWSER && window.isNativeApp === true) {
    let skSessionId = getLocalStorage('x-sk-session-id')
    let accessToken = getLocalStorage('accessToken')
    if (skSessionId) {
      headers = {
        ...headers,
        'x-sk-session-id': skSessionId,
      }
    } else if (accessToken) {
      headers = { ...headers, Authorization: accessToken }
    }
  } else if (!IS_BROWSER && sessionCookies) {
    headers = { ...headers, cookie: sessionCookies }
  }

  // const curAppConfig = getAppConfig()
  // console.log('curAppConfig ', curAppConfig)
  if (!IS_BROWSER && contextName && !curLocaleFromUrl) {
    const logMessage = `Fn fetchResponseFromAPI Current locale is missing in loadParams for SSR, endPointName: ${endPointName}, curLocaleFromUrl: ${curLocaleFromUrl}`
    console.warn(logMessage)
  }

  const curAppConfig = getActiveAppConfig(curLocaleFromUrl)
  const { method, apiUrl } = generateAPIConfig(apiConfig)

  if (
    (!method || !apiUrl) &&
    endPointName !== 'studioData' &&
    endPointName !== 'footerData' &&
    endPointName !== 'liveEventStudioData' &&
    endPointName !== 'csPageEntry'
  ) {
    console.warn('Fetch method or API URL is empty')
    // For studioData, URL is constructed in apiUtils -> fetchResponseFromAPI function
    return {}
  }

  const combinedHeaders = isToIncludeCommomHeaders
    ? getCommonHeaders(headers, endPointName, curAppConfig)
    : {}

  // Need to send cookie over-ride only for orchestration services
  if (
    !combinedHeaders['x-sk-featureflagoverride'] &&
    enableFeatureFlagtoApi() &&
    getCookiesFeatureOverride() !== '' &&
    apiUrl?.includes('/orchestrationservices/')
  ) {
    combinedHeaders['x-sk-featureflagoverride'] = getCookiesFeatureOverride()
  }

  if (!combinedHeaders['Content-Type']) {
    combinedHeaders['Content-Type'] = 'application/json'
  }

  if (deleteContentType === true) {
    delete combinedHeaders['Content-Type']
  }

  if (isToSendLocaleInQueryParam === true) {
    // queryParams.locale = combinedHeaders.locale

    const defaultLocale = getLocaleCodeFromUrl({
      defaultLocale: 'en_US',
      isReverseType: true,
    })
    let localeFromLocalStorage = getLocalStorage?.('locale') || ''

    queryParams.locale = (
      queryParams.locale ||
      curLocaleFromUrl ||
      storeContainer.activeLocale ||
      localeFromLocalStorage ||
      defaultLocale
    )?.replace('-', '_')

    if (IS_BROWSER) {
      const isLoginOrLogoutCallback =
        window.location.pathname.includes(pageNames.loginCallback) ||
        window.location.pathname.includes(pageNames.logoutCallback)
      const loginCallbackLocale =
        getLocalStorage?.('login_callback_locale')?.replace('-', '_') || ''
      const loginCallbackStoreId =
        getLocalStorage?.('login_callback_store_id') || ''

      if (
        isLoginOrLogoutCallback &&
        loginCallbackLocale &&
        loginCallbackStoreId
      ) {
        queryParams.locale = loginCallbackLocale
        queryParams.storeId = loginCallbackStoreId
      }
    }

    delete combinedHeaders.locale
  }

  /**
   * @todo
   * !!! store id mapping should be common
   */
  if (isToSendStoreId) {
    queryParams.storeId = queryParams.storeId || curAppConfig.storeId
    delete combinedHeaders['x-store-id']
  }

  //Remove common headers for specific endpoints cors issue - AWS migration - eg. for /account-orchestrator/graphql
  if (isToRemoveCommonHeaders && isToRemoveCommonHeaders.length !== 0) {
    for (let index = 0; index < isToRemoveCommonHeaders.length; index++) {
      if (isToRemoveCommonHeaders[index] === 'x-store-id') {
        delete combinedHeaders['x-store-id']
      } else if (isToRemoveCommonHeaders[index] === 'locale') {
        delete combinedHeaders['locale']
      }
    }
  }

  let queryPrefix = ''
  let queryParamString = ''
  let queryAddString = ''

  if (addPathParams) {
    queryAddString = addPathParams
  }
  if (pathParams && enableProxy) {
    queryPrefix = '&'
  } else if (Object.keys(queryParams).length > 0) {
    queryPrefix = '?'
  }

  if (queryParams) {
    const searchParams = new URLSearchParams()
    for (const [key, value] of Object.entries(queryParams)) {
      if (value) {
        searchParams.append(key, value)
      }
    }
    queryParamString = queryPrefix + searchParams.toString()
  }

  const path =
    pathParams && enableProxy
      ? `?pathParams=${pathParams}`
      : pathParams
        ? `/${pathParams}`
        : ''

  let personalOffer
  let contextValue
  let mysite
  /**
   * @note
   * replace the cart type name after checking whether it is personal_offer or mysite user
   */
  try {
    if (IS_BROWSER) {
      // const shoppingContext = getLocalStorage('shoppingContext') || {}
      // if (Object.keys(shoppingContext).length > 0) {
      // contextValue = shoppingContext?.context || ''
      contextValue = checkMysiteOrPersonalOffer()
      if (contextValue == 'personal_offer') {
        personalOffer = true
      } else {
        if (contextValue == 'storefront') {
          mysite = true
        }
      }
      // }
    }
  } catch (e) {
    console.error('JSON.parse error at renderCartViewButtons >>> ', e)
  }
  let finalUrl
  if (cartType) {
    // B1 FIX: Remove dynamic require — use already-imported isToShowOverrideSubscription function
    // Dev-mode check - ensure getEffectiveCartType was called upstream
    if (
      process.env.NODE_ENV !== 'production' &&
      cartType === 'USER' &&
      isToShowOverrideSubscription()
    ) {
      console.warn(
        'apiUtils.fetchResponseFromAPI: cartType="USER" passed while subscription override active. ' +
          'Did upstream call getEffectiveCartType()? ' +
          'See: src/models/Checkout/Checkout.js:getEffectiveCartType(). ' +
          'This will route to wrong backend endpoint.'
      )
    }

    const isExpressCartType = isExpressCart(true) && !isFromInit
    const modifiedCartType = isExpressCartType ? 'EXPRESS' : cartType
    const finalURLBasedOnCartTypeInURL = apiUrl?.replace(
      'USER',
      modifiedCartType
    )
    finalUrl = finalURLBasedOnCartTypeInURL + path + queryParamString
  } else {
    if (contextValue) {
      if (personalOffer === true) {
        const isExpressCartType = isExpressCart() && !isFromInit
        const personalOfferApiUrl = isExpressCartType
          ? apiUrl?.replace('USER', 'EXPRESS')
          : apiUrl?.replace('USER', 'POCART')
        finalUrl = personalOfferApiUrl + path + queryParamString
      } else if (mysite === true) {
        const isExpressCartType = isExpressCart() && !isFromInit
        const mysiteOfferApiUrl = isExpressCartType
          ? apiUrl?.replace('USER', 'EXPRESS')
          : apiUrl?.replace('USER', 'MSCART')
        finalUrl = mysiteOfferApiUrl + path + queryParamString
      } else if (
        contextValue === 'MOBILE_CONSUMER' ||
        contextValue === 'MOBILE_AFFILIATE'
      ) {
        // finalURL for VERA & STELA app when context value is present
        finalUrl = apiUrl + path + queryParamString
      } else {
        finalUrl = apiUrl + path + queryParamString
      }
    } else {
      const isSubscriptionOverrideCart = isToShowOverrideSubscription()
      const modifiedURL = isSubscriptionOverrideCart
        ? apiUrl?.replace('USER', 'SUBSCRIPTION_OVERRIDE')
        : apiUrl
      const customURL =
        isExpressCart(true) && !isFromInit
          ? apiUrl?.replace('USER', 'EXPRESS')
          : modifiedURL
      finalUrl = customURL + path + queryParamString
    }
  }

  finalUrl = replaceLocaleInURL({ url: finalUrl })

  try {
    let sponsorIdForMysite = null
    if (IS_BROWSER) {
      const currentUrl = window?.location?.pathname || ''
      const isEqMysiteUrl = currentUrl?.includes('/mysite/') || false
      const isEqMysite = isEqMysiteUrl && enableMysiteRedesignFeature()
      if (isEqMysite) {
        try {
          sponsorIdForMysite = getSessionStorage(SPONSOR) || null
        } catch (err) {
          sponsorIdForMysite = null
          trackErrorInInstana({
            errorReport: 'Error during adding the msSponserId header',
            errorData: e,
          })
        }
      } else if (currentUrl.includes('personal-offer')) {
        const isExpressCartType = isExpressCart() && !isFromInit
        finalUrl = finalUrl?.replace(
          'USER',
          isExpressCartType ? 'EXPRESS' : 'POCART'
        )
      }
    }

    if (sponsorIdForMysite && finalUrl?.includes('/storefront/')) {
      combinedHeaders['x-nuskin-sponsorid'] = sponsorIdForMysite ?? ''
    }
  } catch (e) {
    console.warn('Could not load mysite sponsor for header', e?.message || e)
    trackErrorInInstana({
      errorReport: 'Error during adding the x-nuskin-sponsorid header',
      errorData: e,
    })
  }

  const poHeaderEndpoints = [
    'viewCart',
    'viewCartForCheckout',
    'addToCart',
    'deleteCartItem',
    'updateCartItem',
    'updateBagAttributes',
    'addPaymentToCart',
    'deletePaymentsFromCart',
    'reviewOrder',
    'submitOrder',
    'submitOrderForGlobalPayment',
    'getCatalog',
    'getProducts',
    'getProductbyIdandPromotion',
    'search',
    'topCategory',
    'getInventory',
  ]

  const isSearchEndpoint =
    endPointName === 'search' || endPointName === 'suggestions'
  const shouldApplySearchPOHeaders =
    isSearchEndpoint && contextValue === 'personal_offer'
  const isPOContext =
    enablePORedesignFeature() &&
    poHeaderEndpoints.includes(endPointName) &&
    contextValue === 'personal_offer'

  if (IS_BROWSER && (isPOContext || shouldApplySearchPOHeaders)) {
    try {
      applyPOAnalyticsHeaders(combinedHeaders, {
        includeConvSponsor: isSearchEndpoint,
      })
    } catch (e) {
      console.warn('Error adding PO analytics headers to API', e)
      trackErrorInInstana({
        errorReport: 'Error adding PO/analytics headers to API',
        errorData: e,
      })
    }
  }

  if (IS_BROWSER && endPointName === 'addToCart' && enablePORedesignFeature()) {
    const currentOfferId = resolveActivePOOfferId()
    const poAuditSessionId = getPOAnalyticsSessionId(currentOfferId)
    if (poAuditSessionId) {
      combinedHeaders['x-nuskin-analyticsId'] = poAuditSessionId
    }
  }

  const modifiedHeader = ignoreCommonHeader ? {} : new Headers(combinedHeaders)
  const fetchProps = {
    method,
    headers: modifiedHeader,
    credentials: credentials,
    body:
      combinedHeaders['Content-Type'] === 'application/json'
        ? JSON.stringify(postData)
        : postData,
  }
  if (keepAlive) {
    fetchProps.keepalive = true
  }

  if (mode) {
    fetchProps.mode = mode
  }

  if (!fetchProps.body) {
    delete fetchProps.body
  }

  if (isToDeleteHeader) {
    delete fetchProps.headers
  }

  // Abort API on delay
  let controller = ''
  if (AbortController) {
    controller = new AbortController()
    fetchProps.signal = controller.signal
    if (!IS_BROWSER) {
      setTimeout(() => {
        controller.abort()
      }, API_ABORT_DELAY)
    }
  }
  /*
   * https://nuskin.atlassian.net/browse/CX12-9849
   * below code is to abort the fetch call,
   * if the same api is called again before the previous call is completed
   * when 43 is typed in input, call will be made for 4 and 43
   * so we need to abort the call for 4
   * to handle uncertainties in response time delays
   */
  if (endPointName === 'getProductbyIdandPromotion') {
    const isSubscription =
      postData?.isSubscriptionIncludedInPromotion?.toString() || ''
    const isModalRelatedCall = (
      postData?.isSubscriptionPromotion || false
    ).toString()
    const key =
      endPointName +
      '_isSubscriptionIncludedInPromotion_' +
      isSubscription +
      isModalRelatedCall
    /*
     * we are using the same endpoint for both true and false
     * so appending the isSubscriptionIncludedInPromotion value to the key
     */
    if (application.previousAbortControllers[key]) {
      const oldController = application.previousAbortControllers[key]
      oldController.abort?.()
    }
    application.previousAbortControllers[key] = controller
  }
  if (
    endPointName === 'validateProfileAddress' ||
    endPointName === 'emailLookupMx'
  ) {
    /*
     * we are using the same endpoint for both true and false
     * so appending the isSubscriptionIncludedInPromotion value to the key
     */
    if (application.previousAbortControllers[endPointName]) {
      const oldController = application.previousAbortControllers[endPointName]
      oldController.abort?.()
    }
    application.previousAbortControllers[endPointName] = controller
  }

  /**
   * Logs API response data and headers immediately.
   *
   * This is used to log API results for debugging before further response processing.
   *
   * @param {Object} data - The parsed JSON response body
   * @param {Object} response - The fetch response object
   */
  const logNow = async (data, response) => {
    if (!IS_BROWSER) {
      const { logger } = await import('src/lib/logger')
      const apiName = endPointName || ''
      const apiPath = finalUrl || ''
      const message = data?.message || data?.status || ''
      const traceId = response?.headers?._headers?.['x-b3-traceid'] || []
      const spanId = response?.headers?._headers?.['x-b3-spanid'] || []
      const logMessage = `${apiName} ${apiPath} ${message} x-traceid:${traceId.toString()} x-spanid:${spanId}`
      const isFailure = (data?.status || '') === 'failure'

      logger.error(logMessage, {
        className: `apiUtils.fetchResponseFromAPI >>> ${
          isFailure ? 'FAILED' : 'SUCCESS'
        } `,
      })
    }
  }

  /**
   * Determines if ContentStack error toast should be shown.
   * Reduces cognitive complexity by extracting condition logic.
   *
   * @param {Object} data - Response data containing error code
   * @param {boolean} suppressToast - Flag to suppress the toast
   * @returns {boolean} True if toast should be shown, false otherwise
   */
  const shouldShowErrorToast = (data, suppressToast) => {
    return IS_BROWSER && data.code && !suppressToast
  }

  /**
   * Common handler for successful API responses.
   *
   * @param {Object} response - The fetch response object
   * @param {Object} data - The parsed JSON response body
   */
  const commonAPISucess = async (response, data) => {
    const totalCount = response?.headers?.get?.('x-total-count') || ''
    const status = isSuccessStatus(response) ? 'success' : 'failure'
    statusCode = response?.status

    // log the results for tracing
    // logNow(data, response)
    if (typeof data !== 'object') {
      const responseMessage = data
      data = {
        responseMessage,
      }
    }

    if (status === 'failure') {
      const apiStatusCode = response?.status || ''
      trackApiErrorThroughInstana({
        apiUrl: finalUrl,
        apiConfig,
        statusCode,
        headers: response?.headers,
        responseMessage: data,
      })
    }

    // status is over-written so added additional node to get the response status
    try {
      data.responseStatus = JSON.parse(JSON.stringify(data?.status || ''))
    } catch (error) {
      console.log('data responseStatus', error)
    }

    data.status = status
    data.totalCount = totalCount
    data.statusCode = statusCode

    // HTTP status is unreliable here; gate on data.code, race guards a slow CS lookup
    if (
      shouldShowErrorToast(data, suppressContentStackErrorToast) &&
      enableCSErrorCodeMessage()
    ) {
      const csMessage = await Promise.race([
        getContentStackErrorMessage(data.code, endPointName),
        new Promise(resolve => setTimeout(() => resolve(null), 2000)),
      ])
      if (csMessage) {
        data.message = csMessage
        data.originalCode = data.code
        data.code = ''
        data.status = 'error'
      }
    }

    if (!IS_BROWSER && isToEnableSSR) {
      if (contextName) {
        const responseData = {
          ...data,
          apiUrl: finalUrl,
        }
        if (contextUrlPathFromSSR) {
          responseData.contextUrlPathFromSSR = contextUrlPathFromSSR
        }
        if (contextQueryParamsFromSSR) {
          responseData.contextQueryParamsFromSSR = contextQueryParamsFromSSR
        }
        return Promise.resolve({
          [contextName]: responseData,
        })
      }
    }
    return data
  }
  let statusCode = ''

  /**
   * Handles successful API responses by parsing the response
   * and returning a standardized success object.
   *
   * @param {Object} response - The fetch API response object.
   * @returns {Promise} A promise resolving to the parsed success response object.
   */
  const onAPISuccess = async response => {
    application.IsAppLoading = false
    decrementFetchCount()
    // Setting the session id in header for SSR user based rendering.
    // Removing this we need to use x-sk-signed-user but this by SFO for UI domain
    // const getSessionId = response?.headers?.get?.('x-sk-session-id') || ''
    // setCookie({ cookieName: 'x-sk-session', cookieValue: getSessionId })

    if (endPointName === 'clearOktaUserSession') {
      return response.text().then(data => {
        return commonAPISucess(response, data)
      })
    } else {
      if (
        response.status === 204 ||
        (endPointName === 'deletePromo' && response.status === 202)
      ) {
        if (typeof response.json !== 'function') {
          return commonAPISucess(response, '')
        }
        return response
          .json()
          .then(data => commonAPISucess(response, data))
          .catch(() => commonAPISucess(response, ''))

        // return { status: 'success' }
      } else {
        return response?.json().then(data => {
          return commonAPISucess(response, data)
        })
      }
    }
  }

  /**
   * Handles API request failures by logging the error and tracking it.
   *
   * @param {Object} error - The error object from the failed API request.
   */
  const onAPIFail = error => {
    trackErrorInInstana({
      errorReport: `on API fail ${finalUrl} `,
      errorData: error,
    })

    // console.log(
    //   'onAPIFail error from ' +
    //     (IS_BROWSER ? 'BROWSER ' : 'SERVER ') +
    //     JSON.stringify(apiConfig) +
    //     ' status ' +
    //     statusCode +
    //     ' ' +
    //     error
    // )
    trackApiErrorThroughInstana({
      statusCode,
      apiUrl: finalUrl,
      apiConfig,
      error,
    })
    // console.error('API Request Status: Failed >>> ', finalUrl)
    // console.error('API call error', e)
    application.IsAppLoading = false
    decrementFetchCount()
    return { status: 'failure' }
  }
  const { CS_ID = '', CS_TOKEN = '', CS_ENV = '' } = APPConfig?.getAppConfig()

  incrementFetchCount()
  const csEndPointNames = ['studioData', 'liveEventStudioData', 'csPageEntry']
  if (csEndPointNames.includes(endPointName)) {
    let contentStackLocale =
      queryParams?.locale?.split('_').reverse().join('-') || 'US-en'
    if (contentStackLocale === 'CN-zh') {
      contentStackLocale = 'CA-zh'
    }

    let entryName =
      endPointName === 'liveEventStudioData'
        ? 'ns_live_event_home_page'
        : 'homepage'
    if (csEntryName) {
      entryName = csEntryName
    }
    const csQuery = csQueryString || ''
    let url = `https://cdn.contentstack.io/v3/content_types/${entryName}/entries/?locale=${contentStackLocale}&include_reference_content_type_uid=true&environment=${CS_ENV}${csQuery}`
    finalUrl = url
    console.log(`API Request >>> `, url)
    const contentStackFetchOptions = {
      method: 'GET',
      headers: {
        api_key: CS_ID,
        access_token: CS_TOKEN,
      },
    }
    if (fetchProps?.signal) {
      contentStackFetchOptions.signal = fetchProps.signal
    }
    return await fetch(url, {
      ...contentStackFetchOptions,
    })
      .then(onAPISuccess)
      .catch(onAPIFail)
  } else if (endPointName === 'footerData') {
    let contentStackLocale =
      queryParams?.locale?.split('_')?.reverse()?.join('-') || 'US-en'
    if (contentStackLocale === 'CN-zh') {
      contentStackLocale = 'CA-zh'
    }
    const footerEntryId =
      APPConfig?.getAppConfig()?.footerEntryId || 'blt9de1f7f856382cb2'
    let url = `https://cdn.contentstack.io/v3/content_types/ns_ffg_footer/entries/${footerEntryId}?locale=${contentStackLocale}&environment=${CS_ENV}&include_fallback=true`
    finalUrl = url
    console.log(`API Request >>> `, url)
    const footerFetchOptions = {
      method: 'GET',
      headers: {
        api_key: CS_ID,
        access_token: CS_TOKEN,
      },
    }
    if (fetchProps?.signal) {
      footerFetchOptions.signal = fetchProps.signal
    }
    return await fetch(url, {
      ...footerFetchOptions,
    })
      .then(onAPISuccess)
      .catch(onAPIFail)
  } else if (endPointName === 'csrTranslation') {
    let contentStackLocale =
      queryParams?.locale?.split('_')?.reverse()?.join('-') || 'US-en'
    if (contentStackLocale === 'CN-zh') {
      contentStackLocale = 'CA-zh'
    }
    let url = `https://cdn.contentstack.io/v3/content_types/equinox_all_storefront_csr_translations/entries/blt3927bf010b9955a4?environment=${CS_ENV}&locale=${contentStackLocale}&include[]=csr_all&include_fallback=true&test=2`

    finalUrl = url
    console.log(`API Request >>> `, url)
    const csrTranslationFetchOptions = {
      method: 'GET',
      headers: {
        api_key: CS_ID,
        access_token: CS_TOKEN,
      },
    }
    if (fetchProps?.signal) {
      csrTranslationFetchOptions.signal = fetchProps.signal
    }
    return await fetch(url, {
      ...csrTranslationFetchOptions,
    })
      .then(onAPISuccess)
      .catch(onAPIFail)
  } else {
    try {
      return await fetch(finalUrl, fetchProps)
        .then(onAPISuccess, onAPIFail)
        .catch(onAPIFail)
    } catch (e) {
      console.error(`Fetch exception ${finalUrl}`, e)
    }
  }
}

export async function retriveAgreement(sid, groupKey) {
  const url = `https://pactsafe.io/load/json?sid=${encodeURIComponent(sid)}&gkey=${encodeURIComponent(groupKey)}`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      accept: 'application/json',
    },
  })
  if (!response.ok) {
    throw new Error('Failed to fetch agreed term status')
  }
  return await response.json()
}

export async function checkAgreedTermStatus(sid, sig, groupKey, cid) {
  const url = `https://pactsafe.io/latest?sid=${encodeURIComponent(sid)}&sig=${encodeURIComponent(sig)}&gkey=${encodeURIComponent(groupKey)}&cid=${encodeURIComponent(cid)}`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      accept: 'application/json',
    },
  })
  if (!response.ok) {
    throw new Error('Failed to fetch agreed term status')
  }
  return await response.json()
}

export async function postAgreedTerm(sid, sig, vid, cid) {
  const url = `https://pactsafe.io/send?sid=${encodeURIComponent(sid)}&sig=${encodeURIComponent(sig)}&vid=${vid}&et=agreed&cid=${encodeURIComponent(cid)}`
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      accept: 'application/json',
    },
  })
  if (!response.ok) {
    throw new Error('Failed to fetch agreed term status')
  }
  return await response.json()
}

export {
  fetchResponseFromAPI,
  getLocalStorage,
  setLocalStorage,
  deleteFromLocalStorage,
  setCookie,
  getCookie,
  deleteCookie,
  isB2BAccount,
  isB2BAdmin,
  isB2BUser,
  isB2B2C,
  isB2B2CAdmin,
  enableB2B2CUserInviteCreate,
  isenableUserInvite,
  hasOngoingFetches,
}
export default fetchResponseFromAPI
